'use client';
import { useEffect, useState } from 'react';
import { ArrowLeft, RefreshCw, User, Mail, Briefcase, MessageSquare, Phone, CheckCircle, Clock, List, BarChart3, TrendingUp, Trash2, Download, Users } from 'lucide-react';
import { adminFetch } from '../lib/admin';
import AnalyticsTab from './hiddenleads/AnalyticsTab';
import TrafficTab from './hiddenleads/TrafficTab';
import ReferralsTab from './hiddenleads/ReferralsTab';
import type { Lead } from './hiddenleads/types';

const CF_WORKER_URL = 'https://jkd-whatsapp-verification.jimsbond007.workers.dev';

type TabId = 'leads' | 'analytics' | 'traffic' | 'referrals';

const TABS: { id: TabId; label: string; icon: typeof List }[] = [
  { id: 'leads', label: 'Leads 列表', icon: List },
  { id: 'analytics', label: 'Analytics 分析', icon: BarChart3 },
  { id: 'traffic', label: 'Traffic 流量', icon: TrendingUp },
  { id: 'referrals', label: 'Referrals 推薦', icon: Users },
];

const tabFromHash = (): TabId => {
  const h = window.location.hash;
  return h === '#analytics' ? 'analytics' : h === '#traffic' ? 'traffic' : h === '#referrals' ? 'referrals' : 'leads';
};

export default function HiddenLeadsPage() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>(tabFromHash);

  useEffect(() => {
    const onHashChange = () => setTab(tabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const switchTab = (next: TabId) => {
    window.location.hash = next === 'leads' ? '#leads' : `#${next}`;
  };

  const deleteLead = async (token: string) => {
    if (!window.confirm(`確定刪除這筆 Lead（${token}）？此操作無法還原。`)) return;
    const res = await adminFetch(`/leads/${encodeURIComponent(token)}`, { method: 'DELETE' });
    if (res.ok) {
      setLeads(prev => prev.filter(l => l.token !== token));
    } else {
      const d = await res.json().catch(() => ({}));
      window.alert(`刪除失敗：${d.error || res.status}`);
    }
  };

  const deleteAllLeads = async () => {
    if (!window.confirm(`確定刪除全部 ${leads.length} 筆 Leads？此操作無法還原。建議先匯出 XLSX 備份。`)) return;
    const res = await adminFetch('/leads/delete-all', { method: 'POST' });
    const d = await res.json().catch(() => ({}));
    if (res.ok && d.success) {
      window.alert(`已刪除 ${d.deleted} 筆`);
      fetchLeads();
    } else {
      window.alert(`刪除失敗：${d.error || res.status}`);
    }
  };

  const markWon = async (lead: Lead) => {
    const amount = window.prompt(`標記成交 — 實收金額 HK$（Lead: ${lead.name || lead.token}）：`);
    if (!amount) return;
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) { window.alert('金額無效'); return; }
    const rate = window.prompt('佣金率 override %（留空=按規則 10%/分層）：');
    const body: Record<string, unknown> = { leadToken: lead.token, amount: n };
    if (rate && Number(rate) > 0) body.rateOverride = Number(rate);
    const res = await adminFetch('/deals', { method: 'POST', body: JSON.stringify(body) });
    const d = await res.json().catch(() => ({}));
    if (res.ok && d.success) {
      window.alert(`✅ 成交已登記（${d.deal.id}）— 佣金 HK$${d.deal.commission}（${d.deal.rate}%，待審核）`);
    } else {
      window.alert(`❌ ${d.error || res.status}`);
    }
  };

  const exportXlsx = async () => {
    const XLSX = await import('xlsx');
    const rows = leads.map(l => ({
      Token: l.token,
      Type: l.type === 'contact' ? '聯絡查詢' : '心法通訊',
      Name: l.name || '',
      Email: l.email || '',
      BusinessType: l.businessType || '',
      WhatsApp: l.whatsappNumber || '',
      Locale: l.locale || '',
      Referrer: l.created_referrer || '',
      Verified: l.verified ? 'YES' : 'NO',
      Message: l.message || '',
      SubmittedAt: l.timestamp ? new Date(l.timestamp).toLocaleString('zh-HK') : '',
      VerifiedAt: l.verifiedAt ? new Date(l.verifiedAt).toLocaleString('zh-HK') : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 12 }, { wch: 10 }, { wch: 16 }, { wch: 24 }, { wch: 14 }, { wch: 16 }, { wch: 8 }, { wch: 12 }, { wch: 10 }, { wch: 40 }, { wch: 18 }, { wch: 18 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leads');
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `jkd-leads-${date}.xlsx`);
  };

  const fetchLeads = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${CF_WORKER_URL}/leads`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setLeads(data.leads || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const formatTime = (ts?: number) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleString('zh-HK', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-jkd-black text-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => window.location.href = '/'}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-jkd-gold transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            返回主頁
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={exportXlsx}
              className="inline-flex items-center gap-2 px-4 py-2 border border-jkd-gray-200 text-gray-200 rounded font-bold hover:border-jkd-gold hover:text-jkd-gold transition-colors text-sm"
              disabled={loading || leads.length === 0}
            >
              <Download className="w-4 h-4" />
              匯出 XLSX
            </button>
            <button
              onClick={deleteAllLeads}
              className="inline-flex items-center gap-2 px-4 py-2 border border-red-500/50 text-red-400 rounded font-bold hover:bg-red-500/10 transition-colors text-sm"
              disabled={loading || leads.length === 0}
            >
              <Trash2 className="w-4 h-4" />
              全部刪除
            </button>
            <button
              onClick={fetchLeads}
              className="inline-flex items-center gap-2 px-4 py-2 bg-jkd-gold text-jkd-black rounded font-bold hover:bg-jkd-gold/90 transition-colors"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              重新整理
            </button>
          </div>
        </div>

        <div className="mb-6">
          <h1 className="text-3xl font-black text-white mb-2">Leads 後台</h1>
          <p className="text-gray-400">從聯絡表單與心法通訊收集回來的潛在客戶資料 — 列表檢視與轉化分析</p>
        </div>

        <div className="flex gap-1 mb-8 border-b border-jkd-gray-200">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => switchTab(id)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold rounded-t-lg border-b-2 -mb-px transition-colors ${
                tab === id
                  ? 'border-jkd-gold text-jkd-gold bg-jkd-gray-100/60'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
            載入失敗：{error}
          </div>
        )}

        {tab === 'analytics' && <AnalyticsTab leads={leads} loading={loading} />}

        {tab === 'traffic' && <TrafficTab />}

        {tab === 'referrals' && <ReferralsTab />}

        {tab === 'leads' && (
          <>
            {!loading && leads.length === 0 && !error && (
              <div className="text-center py-20 text-gray-500">
                暫時沒有資料
              </div>
            )}

            <div className="space-y-4">
              {leads.map((lead) => (
                <div key={lead.token} className="card-dark card-gold-border">
                  <div className="flex flex-wrap items-start gap-4 mb-4">
                    <span className={`px-3 py-1 rounded text-xs font-bold ${
                      lead.type === 'contact' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                    }`}>
                      {lead.type === 'contact' ? '聯絡查詢' : '心法通訊'}
                    </span>
                    {lead.verified && lead.type === 'contact' && (
                      <button
                        onClick={() => markWon(lead)}
                        className="px-3 py-1 rounded text-xs font-bold bg-jkd-gold/15 text-jkd-gold hover:bg-jkd-gold/25 transition-colors"
                        title="標記成交：輸入實收金額，自動為綁定夥伴計佣金"
                      >
                        💰 標記成交
                      </button>
                    )}
                    <span className={`px-3 py-1 rounded text-xs font-bold inline-flex items-center gap-1 ${
                      lead.verified ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {lead.verified ? <><CheckCircle className="w-3 h-3" /> 已驗證</> : <><Clock className="w-3 h-3" /> 未驗證</>}
                    </span>
                    <span className="text-gray-500 text-xs font-mono ml-auto">Token: {lead.token}</span>
                    <button
                      onClick={() => deleteLead(lead.token)}
                      className="ml-2 text-gray-500 hover:text-red-400 transition-colors"
                      aria-label="刪除這筆 Lead"
                      title="刪除這筆 Lead"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {lead.name && (
                      <div className="flex items-center gap-3 text-gray-300">
                        <User className="w-4 h-4 text-jkd-gold" />
                        <span>{lead.name}</span>
                      </div>
                    )}
                    {lead.email && (
                      <div className="flex items-center gap-3 text-gray-300">
                        <Mail className="w-4 h-4 text-jkd-gold" />
                        <a href={`mailto:${lead.email}`} className="hover:text-jkd-gold">{lead.email}</a>
                      </div>
                    )}
                    {lead.businessType && (
                      <div className="flex items-center gap-3 text-gray-300">
                        <Briefcase className="w-4 h-4 text-jkd-gold" />
                        <span>{lead.businessType}</span>
                      </div>
                    )}
                    {lead.whatsappNumber && (
                      <div className="flex items-center gap-3 text-gray-300">
                        <Phone className="w-4 h-4 text-jkd-gold" />
                        <a href={`https://wa.me/${lead.whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="hover:text-jkd-gold">
                          {lead.whatsappNumber}
                        </a>
                      </div>
                    )}
                    {lead.created_referrer && (
                      <div className="flex items-center gap-3 text-gray-300">
                        <span className="px-2 py-0.5 rounded bg-jkd-gold/15 text-jkd-gold text-xs font-bold">REF</span>
                        <span className="font-mono text-xs">{lead.created_referrer}</span>
                      </div>
                    )}
                  </div>

                  {lead.message && (
                    <div className="mt-4 pt-4 border-t border-jkd-gray-200">
                      <div className="flex items-start gap-3 text-gray-300">
                        <MessageSquare className="w-4 h-4 text-jkd-gold mt-1" />
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{lead.message}</p>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-500 font-mono">
                    <span>提交：{formatTime(lead.timestamp)}</span>
                    {lead.verifiedAt && <span>驗證：{formatTime(lead.verifiedAt)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
